// The single surface table , doc 42 (docs/expansion-roadmap-2026/42-surface-resolution-and-permission-architecture.md).
//
// Before this file the same rule was written four times (sidebar, mobile nav, dashboard
// quick actions, surface-access.ts) and the four copies had already diverged. Everything a
// surface needs to be gated , does it exist, whose type decides, where does it live, who
// may open it, what does it cost , now lives on ONE row. `resolve-surface.ts` is the only
// reader; consumers ask it, never re-derive.
//
// Client-safe: aliased imports are TYPE-ONLY so this module pulls no server runtime into
// the browser bundle (same constraint as registry.ts).

import { NAV_SURFACES, type NavSurface } from './registry';
import type { OperatingMode } from '@/lib/server/operating-modes';
import type { BillingFeatureKey, PosBillingFeatureKey } from '@/lib/billing/plans';
import type { TeamPermissionKey } from '@/lib/team/permissions';
import type { PackCode } from '@/lib/billing/capability-packs';
import {
  DASHBOARD_ROUTE_HOSTS,
  PLATFORM_HOSTS,
  PLATFORM_HOST_KEYS,
  type PlatformHost,
} from '@/lib/routing/hosts';
import {
  allSurfaces as configSurfaces,
  type SurfaceDefinition as ConfigSurfaceDefinition,
} from '@/lib/config-system';

/**
 * `planned` = declared but not built. Never visible, never linked, never mentioned ,
 * regardless of type, plan or permission. A surface is only allowed to flip to `built`
 * when `scripts/check-surface-invariants.mjs` can prove its route file exists.
 */
export type SurfaceStatus = 'built' | 'planned';

/**
 * Which authority resolves the type (doc 42 D2). `branch` surfaces reshape when the
 * operator switches branch; `business` surfaces are visible if ANY branch's type shows
 * them, because the data behind them never moved.
 */
export type SurfaceScope = 'business' | 'branch' | 'both';

/**
 * Which `app/` mount serves the route. POS surfaces are mounted per branch under
 * /pos/[branchId], everything else under /dashboard.
 *
 * This is a FILE-SYSTEM fact, deliberately separate from `host` below. Doc 32
 * splits the owner surfaces across two hostnames (`dashboard.` for reading data
 * and audits, `manage.` for running the business) that share this one mount, so
 * conflating the two would make it impossible to express the split — and would
 * silently break `DASHBOARD_ROUTE_INDEX`, which needs "is this route under
 * /dashboard", not "which hostname serves it".
 */
export type SurfaceMount = 'dashboard' | 'pos';

/**
 * @deprecated Renamed to `SurfaceMount` — it always described the mount, never a
 * host. Kept as an alias for one release so no import breaks mid-migration.
 */
export type SurfaceRouteHost = SurfaceMount;

/**
 * What flips an `optional` surface visible for a type that doesn't show it by default.
 *
 * `pack` is the doc-04 trigger: a surface row says "visible when this pack is held"
 * (e.g. `quotes` reveals on `catering_pack`). An array names several packs that each
 * grant the same surface (`bookings` is granted by both `catering_pack` and
 * `events_pack`); holding ANY of them reveals it. `pack` is checked before `mode` and
 * `feature` — the purchase is the strongest signal — but the three are OR'd, so a
 * surface revealed by a mode still reveals on that mode for a branch without the pack
 * (CR5's mode-authority rule is untouched).
 */
export type RevealTrigger = {
  mode?: OperatingMode;
  feature?: BillingFeatureKey;
  pack?: PackCode | PackCode[];
};

export interface SurfaceDefinition {
  status: SurfaceStatus;
  scope: SurfaceScope;
  mount: SurfaceMount;
  host: PlatformHost;
  route: string | null;
  routeAliases?: string[];
  /** Path suffix under /dashboard/b/<branchId>/ for `both`-scoped surfaces. */
  branchRoute?: string;
  permissions: TeamPermissionKey[];
  ownerOnly?: boolean;
  requiresFeature?: BillingFeatureKey;
  requiresPosAddon?: boolean;
  requiresPosFeature?: PosBillingFeatureKey;
  reveal?: RevealTrigger;
  subTypes?: Partial<Record<string, 'shown' | 'na'>>;
  lanes?: 'any' | readonly OperatingMode[];
}

// ─── Config-derived table ─────────────────────────────────────────────────────

/**
 * Build the surface definitions from config. The config-system types are slightly
 * wider (strings everywhere), so we narrow here to the strongly-typed local types.
 * Every NavSurface key is guaranteed to exist in the config (verified at build by
 * check-surface-invariants.mjs).
 */
function buildSurfaceDefinitions(): Record<NavSurface, SurfaceDefinition> {
  const result = {} as Record<NavSurface, SurfaceDefinition>;
  for (const key of NAV_SURFACES) {
    const cfg = configSurfaces[key];
    if (!cfg) continue;
    result[key] = {
      status: cfg.status as SurfaceStatus,
      scope: cfg.scope as SurfaceScope,
      mount: cfg.mount as SurfaceMount,
      host: cfg.host as PlatformHost,
      route: cfg.route,
      routeAliases: cfg.routeAliases,
      branchRoute: cfg.branchRoute,
      permissions: cfg.permissions as TeamPermissionKey[],
      ownerOnly: cfg.ownerOnly || undefined,
      requiresFeature: (cfg.requiresFeature ?? undefined) as BillingFeatureKey | undefined,
      requiresPosAddon: cfg.requiresPosAddon || undefined,
      requiresPosFeature: (cfg.requiresPosFeature ?? undefined) as PosBillingFeatureKey | undefined,
      reveal: cfg.reveal as RevealTrigger | undefined,
      subTypes: cfg.subTypes,
      lanes: cfg.lanes as 'any' | readonly OperatingMode[] | undefined,
    };
  }
  return result;
}

export const SURFACE_DEFINITIONS: Record<NavSurface, SurfaceDefinition> = buildSurfaceDefinitions();

export function getSurfaceDefinition(surface: NavSurface): SurfaceDefinition {
  return SURFACE_DEFINITIONS[surface];
}

/** The reveal trigger for an `optional` surface, defaulting to its entitlement. */
export function surfaceReveal(surface: NavSurface): RevealTrigger | undefined {
  const def = SURFACE_DEFINITIONS[surface];
  if (def.reveal) return def.reveal;
  return def.requiresFeature ? { feature: def.requiresFeature } : undefined;
}

// ─── Route resolution ────────────────────────────────────────────────────────

/** Every dashboard path prefix that maps to a surface, longest-first. */
const DASHBOARD_ROUTE_INDEX: Array<{ prefix: string; surface: NavSurface }> = NAV_SURFACES.flatMap(
  (surface) => {
    const def = SURFACE_DEFINITIONS[surface];
    if (!def || def.status !== 'built' || def.mount !== 'dashboard' || !def.route) return [];
    return [def.route, ...(def.routeAliases ?? [])].map((prefix) => ({ prefix, surface }));
  },
).sort((a, b) => b.prefix.length - a.prefix.length);

/**
 * Resolve which surface (if any) a dashboard pathname belongs to. Longest prefix wins so
 * /dashboard/operations/inventory isn't shadowed by a broader /dashboard/operations entry.
 *
 * Branch-scoped paths (`/dashboard/b/<branchId>/…`, doc 05 §4) are resolved by
 * stripping the branch segment first, so a branch-workspace route maps to the
 * same surface as its flat counterpart. The branch id itself is validated by
 * the branch layout (server) and the route guard (client), not here.
 */
export function resolveSurfaceForPath(pathname: string | null | undefined): NavSurface | null {
  if (!pathname) return null;
  const branchStripped = pathname.replace(/^\/dashboard\/b\/[^/]+/, '/dashboard');
  const match = DASHBOARD_ROUTE_INDEX.find(
    (entry) =>
      branchStripped === entry.prefix || branchStripped.startsWith(`${entry.prefix}/`),
  );
  return match?.surface ?? null;
}

/**
 * Which HOST serves a path — the resolver the cross-host 308 depends on (doc 32 §4.5).
 *
 * `dashboard.` and `manage.` share the /dashboard mount, so a path's first segment
 * cannot decide between them. Resolution order:
 *
 *   1. the SURFACE that owns the route, and `DASHBOARD_ROUTE_HOSTS`, are consulted
 *      together — whichever matched the LONGER prefix wins
 *   2. path-segment ownership in the host map, for every other host
 *
 * Step 1 compares specificity rather than trying one table then the other, because
 * the two overlap at different depths. `/dashboard/settings/import` is prefix-
 * matched by the `settings` surface (`/dashboard/settings`, host `dashboard`) AND
 * by the override for the importer (host `manage`). Consulting surfaces first would
 * silently hand the importer to `dashboard.`, and the more specific rule — the one
 * a human wrote to say exactly this — would never be reachable.
 *
 * Returns `null` only for paths no host claims — which the invariant check treats
 * as a failure rather than a default, because a defaulted path is a page silently
 * reachable at two addresses.
 */
/**
 * Like `resolveHostForPath`, but reports HOW the match was made.
 *
 * `strength: 'exact'` means a real surface route or a specific override claimed it.
 * `'catch-all'` means only the bare `/dashboard` fallback did — i.e. "some owner
 * page, we do not know which". The router needs that distinction: on a mounted host
 * a host-relative path must be interpreted in that host's namespace FIRST, but a
 * catch-all match is too weak to override a genuine claim from another host.
 */
export function resolveHostForPathDetailed(
  pathname: string | null | undefined,
): { host: PlatformHost; strength: 'exact' | 'catch-all' } | null {
  if (!pathname) return null;
  const path = pathname.split(/[?#]/)[0];
  const matches = (prefix: string) => path === prefix || path.startsWith(`${prefix}/`);

  const candidates: Array<{ prefix: string; host: PlatformHost }> = [];
  const surface = resolveSurfaceForPath(path);
  if (surface) {
    const def = SURFACE_DEFINITIONS[surface];
    for (const route of [def.route, ...(def.routeAliases ?? [])]) {
      if (route) candidates.push({ prefix: route, host: def.host });
    }
  }
  candidates.push(...DASHBOARD_ROUTE_HOSTS);

  const best = candidates
    .filter((entry) => matches(entry.prefix))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];

  if (best) {
    return { host: best.host, strength: isBareMount(best.prefix) ? 'catch-all' : 'exact' };
  }

  const segment = path.split('/').filter(Boolean)[0];
  if (!segment) return null;
  for (const key of PLATFORM_HOST_KEYS) {
    if (PLATFORM_HOSTS[key].owns.includes(segment)) {
      // Same rule as above, one level down: a host whose `owns` entry is simply its
      // own mount segment has told us "something under this mount", not which page.
      return { host: key, strength: isBareMount(`/${segment}`) ? 'catch-all' : 'exact' };
    }
  }
  return null;
}

/**
 * Is this prefix nothing but some host's bare mount?
 *
 * A bare mount is the weakest possible claim — it says "some page under this
 * mount" and never which one. This was hard-coded to `/dashboard`, the only mount
 * two hosts share, and that omission had teeth: on auth.menuraq.com the path
 * `/dashboard` was read in auth's own namespace as `/auth/dashboard`, matched
 * auth's mount prefix, was graded `exact` — and rewrote to a route that does not
 * exist. Every auth exit that pushed the literal `/dashboard` 404'd on the host
 * whose entire job is to send people somewhere else afterwards.
 */
function isBareMount(prefix: string): boolean {
  return PLATFORM_HOST_KEYS.some((key) => PLATFORM_HOSTS[key].mountPath === prefix);
}

export function resolveHostForPath(pathname: string | null | undefined): PlatformHost | null {
  if (!pathname) return null;
  const path = pathname.split(/[?#]/)[0];

  const candidates: Array<{ prefix: string; host: PlatformHost }> = [];

  const surface = resolveSurfaceForPath(path);
  if (surface) {
    const def = SURFACE_DEFINITIONS[surface];
    for (const route of [def.route, ...(def.routeAliases ?? [])]) {
      if (route) candidates.push({ prefix: route, host: def.host });
    }
  }
  candidates.push(...DASHBOARD_ROUTE_HOSTS);

  const best = candidates
    .filter((entry) => path === entry.prefix || path.startsWith(`${entry.prefix}/`))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];

  if (best) return best.host;

  const segment = path.split('/').filter(Boolean)[0];
  if (!segment) return null;
  for (const key of PLATFORM_HOST_KEYS) {
    if (PLATFORM_HOSTS[key].owns.includes(segment)) return key;
  }
  return null;
}
